import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Search, 
  Barcode,
  Package,
  Plus,
  Check,
  AlertCircle,
  Loader2,
  Heart,
  ShoppingCart
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { itemService, ExternalItem } from '../services/itemService';
import LoadingSpinner from './LoadingSpinner';

interface AddItemModalProps {
  onClose: () => void;
  onItemAdded: () => void;
}

type SearchType = 'term' | 'sku' | 'barcode';

const AddItemModal: React.FC<AddItemModalProps> = ({ onClose, onItemAdded }) => {
  const { user } = useAuth();
  const { currentStore } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('term');
  const [searchResults, setSearchResults] = useState<ExternalItem[]>([]);
  const [favoriteItems, setFavoriteItems] = useState<ExternalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'search' | 'favorites'>('search');
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearch, setHasMoreSearch] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ExternalItem | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [unitCost, setUnitCost] = useState(0);
  const [unitPrice, setUnitPrice] = useState(0);
  const [seenItemIds, setSeenItemIds] = useState<Set<number>>(new Set()); // Track seen items for deduplication
  const quantityInputRef = useRef<HTMLInputElement>(null);

  // Auto-detect search type based on input content
  const detectSearchType = (value: string): SearchType => {
    if (!value.trim()) return 'term'; // Default to text search when empty
    
    const isNumeric = /^\d+$/.test(value);
    
    // If only numbers less than 5 digits → SKU  
    if (isNumeric && value.length < 5) return 'sku';
    
    // If only numbers 5+ digits → Barcode
    if (isNumeric && value.length >= 5) return 'barcode';
    
    // Everything else (letters, words, mixed content) → Text search
    return 'term';
  };

  // Handle search input change with auto-detection
  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    const detectedType = detectSearchType(value);
    setSearchType(detectedType);
    
    // Reset seen items when search term changes
    setSeenItemIds(new Set());
    
    // Auto-search when input changes (debounced)
    if (value.trim()) {
      handleAutoSearch(value, detectedType);
    } else {
      setSearchResults([]);
      setError(null);
    }
  };

  // Debounced auto-search
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const handleAutoSearch = (term: string, type: SearchType) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (term.trim()) {
        const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
        handleSearch(syntheticEvent, false, term, type);
      }
    }, 500); // Wait 500ms after user stops typing
    
    setSearchTimeout(timeout);
  };

  // Load favorite items on component mount
  useEffect(() => {
    loadFavoriteItems();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Auto-focus and select quantity input when modal opens
  useEffect(() => {
    if (showQuantityModal && quantityInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select(); // This selects all text
        }
      }, 100);
    }
  }, [showQuantityModal]);

  const loadFavoriteItems = async () => {
    setLoading(true);
    try {
      const result = await itemService.getFavoriteItems(1, 50); // Load more items since no pagination
      if (result.data && Array.isArray(result.data)) {
        setFavoriteItems(result.data);
      }
    } catch (err) {
      console.error('Error loading favorite items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent, append: boolean = false, term?: string, type?: SearchType) => {
    e.preventDefault();
    const currentSearchTerm = term || searchTerm;
    const currentSearchType = type || searchType;

    if (!currentSearchTerm.trim()) return;

    setLoading(true);
    setError(null);

    if (!append) {
      setSearchResults([]);
      setSearchPage(1);
    }

    const currentPage = append ? searchPage : 1;

    try {
      let result;
      // Pass current store name as preferred outlet for pricing
      const preferredOutlet = currentStore?.name;

      switch (currentSearchType) {
        case 'barcode':
          result = await itemService.searchByBarcode(currentSearchTerm.trim(), currentPage, 20, preferredOutlet);
          break;
        case 'sku':
          result = await itemService.searchBySku(currentSearchTerm.trim(), currentPage, 20, preferredOutlet);
          break;
        default:
          result = await itemService.searchByTerm(currentSearchTerm.trim(), currentPage, 20, preferredOutlet);
          break;
      }

      if (result.error) {
        setError('Failed to search items. Please try again.');
        console.error('Search error:', result.error);
      } else if (result.data) {
        // Handle paginated response
        const items = result.data.items || result.data; // Fallback for non-paginated responses
        const hasNext = result.data.hasNext !== undefined ? result.data.hasNext : false;
        
        // Deduplicate items based on ID (for exact searches that might return same items)
        let filteredItems = items;
        if (append) {
          // When appending, filter out items we've already seen
          filteredItems = items.filter((item: ExternalItem) => !seenItemIds.has(item.id));
          setSearchResults(prev => [...prev, ...filteredItems]);
          // Update seen items
          setSeenItemIds(prev => {
            const newSet = new Set(prev);
            filteredItems.forEach((item: ExternalItem) => newSet.add(item.id));
            return newSet;
          });
        } else {
          // New search - reset seen items and use all items
          setSearchResults(filteredItems);
          setSeenItemIds(new Set(filteredItems.map((item: ExternalItem) => item.id)));
        }
        
        setHasMoreSearch(hasNext);
        if (append) {
          setSearchPage(prev => prev + 1);
        }

        if ((!append) && filteredItems.length === 0) {
          setError('No items found. Try a different search term.');
        }
      }
    } catch (err) {
      setError('Failed to search items. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMoreSearch = () => {
    if (!loading && hasMoreSearch && searchTerm.trim()) {
      // Create a synthetic form event for consistency
      const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSearch(syntheticEvent, true);
    }
  };

  const handleAddItem = async (item: ExternalItem) => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    if (!currentStore?.id) {
      setError('No store selected');
      return;
    }

    // Check if item already exists in this store
    setLoading(true);
    try {
      const result = await itemService.checkItemExistsInStore(item.sku, currentStore.id);
      
      if (result.error) {
        setError('Error checking item existence. Please try again.');
        return;
      }

      if (result.data?.exists) {
        setError(`Item "${result.data.item_name}" already exists in ${currentStore.name} with ${result.data.current_stock} units in stock. Use inventory adjustment to modify stock levels.`);
        return;
      }

      // Item doesn't exist, proceed with adding
      const itemPrice = item.price || 0;
      const itemCost = itemPrice * 0.7; // Default cost as 70% of price

      setSelectedItem(item);
      setQuantity(1);
      setUnitPrice(itemPrice);
      setUnitCost(itemCost);
      setShowQuantityModal(true);
      setError(null); // Clear any previous errors
    } catch (error) {
      setError('Error checking item existence. Please try again.');
      console.error('Error checking item existence:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAddItem = async () => {
    if (!selectedItem || !user?.id || !currentStore?.id) {
      setError('Missing required information');
      return;
    }

    setAdding(selectedItem.id);
    setError(null);
    setSuccess(null);
    setShowQuantityModal(false);

    try {
      const result = await itemService.addItemToDatabase(
        selectedItem, 
        user.id,
        currentStore.id,
        quantity,
        unitCost,
        unitPrice
      );
      
      if (result.error) {
        setError(`Failed to add item: ${result.error.message || 'Unknown error'}`);
        console.error('Add item error:', result.error);
      } else {
        setSuccess(`Successfully added ${quantity} units of "${selectedItem.name}" to ${currentStore.name} inventory!`);
        onItemAdded(); // Refresh the parent list
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to add item. Please try again.');
      console.error('Add item error:', err);
    } finally {
      setAdding(null);
      setSelectedItem(null);
    }
  };

  const handleCancelAddItem = () => {
    setShowQuantityModal(false);
    setSelectedItem(null);
    setQuantity(1);
    setUnitCost(0);
    setUnitPrice(0);
  };

  const renderItemCard = (item: ExternalItem, source: 'search' | 'favorite') => (
    <div key={`${source}-${item.id}`} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
      {/* Hero Image */}
      <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
        {(item.image_url || item.extra_image_url) ? (
          <img
            src={item.image_url || item.extra_image_url}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        
        {/* Fallback when no image or image fails to load */}
        <div className={`absolute inset-0 flex items-center justify-center ${(item.image_url || item.extra_image_url) ? 'hidden' : 'flex'}`}>
          <Package className="w-16 h-16 text-gray-400 group-hover:text-gray-500 transition-colors" />
        </div>

        {/* Price badge */}
        {item.price && (
          <div className="absolute top-3 left-3">
            <span className="bg-green-600 text-white text-sm font-semibold px-3 py-1 rounded-full shadow-lg">
              MVR {item.price.toFixed(2)}
            </span>
          </div>
        )}

        {/* Category badge */}
        {item.category && (
          <div className="absolute top-3 right-3">
            <span className="bg-black/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
              {item.category}
            </span>
          </div>
        )}

        {/* Add button overlay */}
        <div className="absolute bottom-3 right-3">
          <button
            onClick={() => handleAddItem(item)}
            disabled={adding === item.id}
            className="bg-primary-600 hover:bg-primary-700 text-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-70"
          >
            {adding === item.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title and Brand */}
        <div className="mb-3">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1">
            {item.name}
          </h3>
          {item.brand && (
            <p className="text-xs text-gray-500 font-medium">{item.brand}</p>
          )}
        </div>

        {/* SKU and Barcode */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">SKU:</span>
            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-700">
              {item.sku}
            </span>
          </div>
          
          {item.barcode && (
            <div className="flex items-center gap-2">
              <Barcode className="w-3 h-3 text-gray-400" />
              <span className="text-xs font-mono text-gray-600">{item.barcode}</span>
            </div>
          )}
        </div>

        {/* Removed description as requested - avoiding small details */}

        {/* Stock info for outlets - only show for POS items, not favorites */}
        {item.product_outlets && item.product_outlets.length > 0 && (
          <div className="space-y-1 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-700 mb-2">Stock Levels:</p>
            {item.product_outlets.map((outlet, index) => (
              <div key={index} className="flex justify-between items-center text-xs">
                <span className="text-gray-600">{outlet.outlet_name}:</span>
                <span className={`font-medium px-2 py-0.5 rounded ${
                  outlet.available_inventory > 0 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {outlet.available_inventory} units
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Note for favorite items */}
        {!item.product_outlets && source === 'favorite' && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 italic">From favorite items - pricing and stock info will be available after adding</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header - Compact on mobile */}
          <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 hidden sm:block">Add Items from POS</h2>
            <h2 className="text-base font-semibold text-gray-900 sm:hidden">Add Items</h2>
            <button
              onClick={onClose}
              className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(90vh-120px)]">
            {/* Status Messages */}
            {error && (
              <div className="mb-3 sm:mb-4 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-red-700">{error}</span>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-3 sm:mb-4 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-green-700">{success}</span>
                </div>
              </div>
            )}

            {/* Header with Search and Tab Icons */}
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                {/* Search Field and Type */}
                <div className="flex-1 flex gap-2 sm:gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleSearchInputChange(e.target.value)}
                      placeholder={
                        searchType === 'barcode' 
                          ? 'Barcode numbers...' 
                          : searchType === 'sku'
                          ? 'SKU numbers...'
                          : 'Search products...'
                      }
                      className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
                      autoFocus
                    />
                  </div>
                  
                  {/* Search Type - Icons on mobile */}
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as SearchType)}
                    className="px-2 sm:px-3 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm min-w-[60px] sm:min-w-[140px]"
                  >
                    <option value="term">🔍 Text</option>
                    <option value="sku">📦 SKU</option>
                    <option value="barcode">🏷️ Barcode</option>
                  </select>
                </div>

                {/* Tab Icons - Icons only on mobile */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1 self-center sm:self-auto">
                  <button
                    onClick={() => setActiveTab('search')}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all duration-200 ${
                      activeTab === 'search'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    title="Search POS Items"
                  >
                    <Search className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">Search</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('favorites')}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all duration-200 ${
                      activeTab === 'favorites'
                        ? 'bg-white text-red-500 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    title="Favorite Items"
                  >
                    <Heart className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">Favorites</span>
                  </button>
                </div>
              </div>

              {/* Search Type Indicator - More compact on mobile */}
              {searchTerm && (
                <div className="mt-2 sm:mt-3 text-xs text-gray-500 bg-blue-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md border border-blue-100">
                  <span className="flex items-center gap-1 sm:gap-2">
                    {searchType === 'barcode' && <Barcode className="w-3 h-3 text-blue-500" />}
                    {searchType === 'sku' && <Package className="w-3 h-3 text-blue-500" />}
                    {searchType === 'term' && <Search className="w-3 h-3 text-blue-500" />}
                    <span className="hidden sm:inline">Auto-detected:</span>
                    <span className="sm:hidden">Type:</span>
                    <span className="font-medium capitalize">
                      {searchType === 'term' ? 'Text' : searchType === 'sku' ? 'SKU' : 'Barcode'}
                    </span>
                    {loading && <span className="text-blue-600">• Searching...</span>}
                  </span>
                </div>
              )}
            </div>

            {/* Search Tab */}
            {activeTab === 'search' && (
              <>

                {/* Search Results */}
                {loading && searchResults.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                      {searchResults.map((item) => renderItemCard(item, 'search'))}
                    </div>
                    
                    {/* Load More Search Results */}
                    {hasMoreSearch && (
                      <div className="text-center pt-4 sm:pt-6 mt-4 sm:mt-6 border-t border-gray-200">
                        <button
                          onClick={handleLoadMoreSearch}
                          disabled={loading}
                          className="btn-secondary px-4 sm:px-6 py-2 text-sm"
                        >
                          {loading ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                    
                    {/* Loading More Indicator */}
                    {loading && searchResults.length > 0 && (
                      <div className="text-center pt-3 sm:pt-4">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}
                  </>
                ) : searchTerm && !loading ? (
                  <div className="text-center py-6 sm:py-8">
                    <Package className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base px-4">No items found. Try a different search term.</p>
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Search className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base px-4">Start typing to search items from your POS system.</p>
                  </div>
                )}
              </>
            )}

            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <>
                {loading && favoriteItems.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : favoriteItems.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                    {favoriteItems.map((item) => renderItemCard(item, 'favorite'))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base px-4">No favorite items available.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quantity Input Modal */}
      {showQuantityModal && selectedItem && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" onClick={handleCancelAddItem} />
          
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
                Add to Stock
              </h3>
              <button
                onClick={handleCancelAddItem}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Item Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 text-sm mb-1">{selectedItem.name}</h4>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>SKU: {selectedItem.sku}</span>
                  {selectedItem.barcode && <span>Barcode: {selectedItem.barcode}</span>}
                </div>
                {selectedItem.brand && (
                  <div className="text-xs text-gray-500 mt-1">Brand: {selectedItem.brand}</div>
                )}
              </div>

              {/* Store Info */}
              <div className="mb-4 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                Adding to: <span className="font-medium">{currentStore?.name}</span>
              </div>

              {/* Input Fields */}
              <div className="space-y-4">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity *
                  </label>
                  <input
                    ref={quantityInputRef}
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    onFocus={(e) => e.target.select()} // Select all text when focused
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Unit Cost */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Cost (MVR)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitCost}
                    onChange={(e) => setUnitCost(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Your cost price for this item</p>
                </div>

                {/* Unit Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Price (MVR)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Selling price for this item</p>
                </div>

                {/* Total Summary */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-green-800 mb-1">Stock Entry Summary:</div>
                  <div className="text-xs text-green-700 space-y-1">
                    <div>Quantity: {quantity} units</div>
                    <div>Total Cost: MVR {(quantity * unitCost).toFixed(2)}</div>
                    <div>Total Value: MVR {(quantity * unitPrice).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCancelAddItem}
                className="btn-secondary px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAddItem}
                disabled={adding === selectedItem.id}
                className="btn-primary px-6 py-2 flex items-center gap-2"
              >
                {adding === selectedItem.id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add to Stock
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddItemModal;